import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Catches render-time errors so a single broken component cannot take the
 * whole console down to a blank screen.
 *
 * This exists because it did: a throw inside the live map unmounted the tree
 * and left nothing but a black page and a console message nobody was looking
 * at. An operator staring at a dark screen during a shift needs to be told
 * what broke and be able to get back to work.
 */
interface Props {
  children: ReactNode
  /** Shown instead of the whole-page treatment when a panel fails. */
  compact?: boolean
}

interface State {
  error: Error | null
  info: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info })
    // Keep it in the console too, where a developer will look for the stack.
    console.error('Unhandled render error:', error, info.componentStack)
  }

  private reset = () => this.setState({ error: null, info: null })

  render() {
    const { error, info } = this.state
    if (!error) return this.props.children

    return (
      <div
        className={
          this.props.compact
            ? 'panel panel-bracket p-5'
            : 'h-full grid place-items-center p-6 overflow-auto'
        }
      >
        <div className="panel panel-bracket max-w-2xl w-full p-6 space-y-4">
          <div className="flex items-center gap-3">
            <span
              className="w-8 h-8 grid place-items-center rounded-[3px] border text-[15px]"
              style={{
                color: 'var(--text-critical)',
                borderColor: 'color-mix(in srgb, var(--color-critical) 35%, transparent)',
                background: 'color-mix(in srgb, var(--color-critical) 10%, transparent)',
              }}
            >
              !
            </span>
            <div>
              <h1 className="text-[15px] font-semibold tracking-tight">This screen failed to render</h1>
              <p className="text-[12px] text-[var(--color-ink-3)]">
                The rest of the application is unaffected.
              </p>
            </div>
          </div>

          <div
            className="px-3 py-2.5 rounded-[3px] font-[var(--font-mono)] text-[12px] leading-relaxed overflow-auto max-h-48"
            style={{
              background: 'var(--color-hull)',
              color: 'var(--text-critical)',
              border: '1px solid var(--color-rule)',
            }}
          >
            {error.message}
          </div>

          {info?.componentStack && (
            <details className="text-[11px] text-[var(--color-ink-3)]">
              <summary className="cursor-pointer label">Component stack</summary>
              <pre className="mt-2 overflow-auto max-h-56 text-[10px] leading-relaxed whitespace-pre-wrap">
                {info.componentStack}
              </pre>
            </details>
          )}

          <div className="flex gap-2 pt-1">
            <button className="btn btn-primary" onClick={this.reset}>
              Try again
            </button>
            <button className="btn" onClick={() => window.location.assign('/')}>
              Back to the dashboard
            </button>
            <button className="btn btn-ghost" onClick={() => window.location.reload()}>
              Reload
            </button>
          </div>
        </div>
      </div>
    )
  }
}
